import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, NavLink, Link } from 'react-router-dom';
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  AppBar,
  Toolbar,
  Button,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../img/motionair_icon_blue.svg';
import NotificationBell from '../Components/NotificationBell.jsx';
import FallingFlakes from '../Components/ChristmasComponents/FallingFlakes.jsx';
import { useAuth } from '../Hooks/useAuth';
import isElectron from '../Utils/isElectron';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

/**
 * App header layout with navigation, offline indicator (next to sync icon),
 * and conditional Electron UI. The offline badge mirrors network status
 * and is placed left of the notifications/sync icon.
 */
const Layout = ({ hideHeaderPaths = [] }) => {
  const { handleLogout, user, loading: authLoading } = useAuth();
  // FIX 1: We will ONLY use pathname from useLocation for all path logic.
  const { pathname } = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [userRole, setUserRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);

  // Initialize Firebase Functions
  useEffect(() => {
    const functions = getFunctions();
    if (process.env.NODE_ENV === 'development') {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
  }, []);

  // Get user role when user is authenticated
  useEffect(() => {
    const getUserRole = async () => {
      if (!user) {
        setRoleLoading(false);
        return;
      }

      try {
        const functions = getFunctions();
        const getUserRoleFunction = httpsCallable(functions, 'getUserRole');
        const response = await getUserRoleFunction();
        setUserRole(response.data.role);
      } catch (error) {
        console.error('[Layout] Error getting user role:', error);
        setUserRole(null);
      } finally {
        setRoleLoading(false);
      }
    };

    getUserRole();
  }, [user]);

  // This check now correctly uses the pathname from the hook
  // Hide header ONLY if user is definitely a customer (not while loading)
  // Show header for employees, while loading, or if no user
  const isCustomer = user && !authLoading && !roleLoading && userRole === 'customer';
  const showHeader = !hideHeaderPaths.includes(pathname) && !isCustomer;

  // This check is for conditionally rendering the Electron window controls
  const isElectronEnv = isElectron();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const navLinks = [
    { path: "/calendar", text: "Calendar" },
    { path: "/agenda", text: "Agenda" },
    { path: "/todo", text: "Todo" },
    { path: "/resources", text: "Resources" },
    // Only show settings in web app, not in Electron
    ...(isElectronEnv ? [] : [{ path: "/settings", text: "Settings" }]),
  ];

  const handleOpenSettings = () => {
    if (window.electronAPI && window.electronAPI.openSettingsWindow) {
      window.electronAPI.openSettingsWindow();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {showHeader && (
        <AppBar
          position="fixed"
          sx={{
            backgroundColor: "#2e2e2e",
            boxShadow: "none",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            overflow: 'visible', // Allow flakes to be visible
            '&.MuiAppBar-root': {
              backgroundColor: "#2e2e2e",
            },
          }}
        >
          {/* Falling Snow Animation */}
            <FallingFlakes height={64} />
          <Toolbar sx={{ justifyContent: "space-between", paddingX: "16px", position: "relative", zIndex: 2 }}>
            {/* FIX 2: conditionally render a spacer for the electron window controls */}
            {isElectronEnv ? (
              <div style={{width: "20px"}}></div>
            ) : (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleMobileMenu}
                sx={{ display: { md: "none" } }}
              >
                <MenuIcon sx={{ color: "#3498B7" }} />
              </IconButton>
            )}

            {!isElectronEnv && (
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
                <Link to="/calendar">
                  <img
                    className="logo-image"
                    src={logo}
                    alt="Logo"
                    style={{ height: "40px" }}
                  />
                </Link>
                {/* Optional left-side indicator removed per new requirements */}
              </Box>
            )}
            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                marginRight: "auto",
                marginLeft: "20px",
              }}
            >
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive ? "active regular" : "regular"
                  }
                  style={{ marginRight: "20px" }}
                >
                  {link.text}
                </NavLink>
              ))}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 'auto', justifyContent: 'flex-end' }} style={{ WebkitAppRegion: 'no-drag' }}>
              {/* Show logout on web only; hide in Electron */}
              {!isElectronEnv && (
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <div className="log-out-container">
                    <button className="log-out-btn" onClick={handleLogout}>
                      Log-out
                    </button>
                  </div>
                </Box>
              )}
              {!isOnline && (
                <div
                  style={{
                    background: 'rgba(200, 30, 30, 0.95)',
                    color: '#fff',
                    borderRadius: 8,
                    padding: '2px 10px',
                    fontSize: 12,
                    lineHeight: '16px',
                    minWidth: 86,
                    textAlign: 'center',
                    WebkitAppRegion: 'no-drag'
                  }}
                >
                  Offline
                </div>
              )}
              <NotificationBell inline showWhenOnline={false} />
              {/* Top-right offline badge is globally rendered in App.js; removed local wrapper */}
            </Box>

            {/* Removed mobile spacer; right content is right-aligned */}
          </Toolbar>
        </AppBar>
      )}

      {/* This Toolbar acts as a spacer to push content below the fixed AppBar */}
      {showHeader && <Toolbar />}

      {/* Mobile Drawer Navigation */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { md: "none" },
          '& .MuiDrawer-paper': {
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' }
          }
        }}
      >
        <Box
          sx={{ width: 250, backgroundColor: "#2e2e2e", height: "100%" }}
          role="presentation"
          onClick={toggleMobileMenu}
          onKeyDown={toggleMobileMenu}
        >
          {/* Mobile-only logo now shown inside the drawer */}
          {!isElectronEnv && (
            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'center', py: 2 }}>
              <Link to="/calendar">
                <img
                  className="logo-image"
                  src={logo}
                  alt="Logo"
                  style={{ height: "40px" }}
                />
              </Link>
            </Box>
          )}
          <List>
            {navLinks.map((link) => (
              <ListItem key={link.text} disablePadding>
                <ListItemButton component={Link} to={link.path}>
                  <ListItemText
                    primary={link.text}
                    sx={{
                      color: pathname === link.path ? "#3598b7" : "#D4D4D4",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <List sx={{ marginTop: "auto" }}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemText primary="Log out" sx={{ color: "#D4D4D4" }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <main className="app-content">
        <Outlet />
      </main>
    </Box>
  );
};

export default Layout;
